import React, { useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import { Box, Button, Collapse, ListItem, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const useStyles = makeStyles((theme) => ({
  item: {
    display: "block",
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: "#fff",
    justifyContent: "flex-start",
    textTransform: "none",
    marginBottom: "8px",
    letterSpacing: 0,
    width: "100%",
    fontWeight: 400,
    "&:hover": {
      color: "#fff",
      background: "rgba(255, 255, 255, 0.15)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      "& $icon": {
        color: "#fff",
      },
    },
  },
  buttonLeaf: {
    color: "#fff !important",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
    marginBottom: "5px",
    opacity: 1,
    border: "1px solid transparent !important",
    borderRadius: "10px",
    fontWeight: 400,
    fontSize: "13px",
    transition: "all 0.3s ease-in-out",
    transform: "translateX(0px)",

    "& $icon": {
      color: "#fff",
      transition: "all 0.3s ease-in-out",
    },
    "& li": {
      "& $title": {
        marginLeft: "30px",
      },
    },
    "&:hover, &.active": {
      borderRadius: "12px",
      border: "1px solid rgba(255, 255, 255, 0.3) !important",
      background: "rgba(255, 255, 255, 0.15)",
      color: "#fff !important",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      transform: "translateX(4px)",
      transition: "all 0.3s ease-in-out",
      backdropFilter: "blur(10px)",
      "& $icon": {
        color: "#fff !important",
        transform: "scale(1.1)",
      },
      "& span": {
        color: "#fff !important",
        fontWeight: "600",
        "& img": {
          filter: "brightness(0) invert(1)",
        },
      },
    },
    "&.depth-0": {
      "& $title": {
        fontWeight: 400,
        fontSize: "12px",
        whiteSpace: "pre",
      },
    },
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(1),
    color: "#fff",
  },
  title: {
    marginRight: "auto",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "400",
  },
  IcoBoxesMain:{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "transparent",
    width: "30px",
    height: "35px",
    borderRadius: "50%",
    "& span": {
      color: "#fff",
      fontWeight: 400,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& img": {
        filter: "brightness(0) invert(1) !important",
        transition: "filter 0.2s ease",
      },
      "& svg": {
        color: "#fff !important",
        fill: "#fff !important",
        stroke: "#fff !important",
      }
    },
  },
  active: {
    color: "#fff !important",
    background: "rgba(255, 255, 255, 0.2)",
    borderRadius: "12px",
    fontWeight: theme.typography.fontWeightRegular,
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
    transform: "translateX(6px)",
    border: "1px solid rgba(255, 255, 255, 0.4) !important",
    backdropFilter: "blur(10px)",
    "& $title": {
      fontWeight: theme.typography.fontWeightMedium,
      color: "#fff !important",
    },
    "& $icon": {
      color: "#fff !important",
      transform: "scale(1.15)",
    },
    "& span": {
      color: "#fff !important",
      fontWeight: "700",
    },
  },
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  isLogout,
  onLogout,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={{ color: "#ccc" }}
        >
          {Icon && (
            <span className={classes.icon}>
              {typeof Icon === 'string' ? <img src={Icon} alt={`${Icon}Img`} /> : <Icon size={20} />}
            </span>
          )}

          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <>
      {" "}
      <ListItem
        className={clsx(classes.itemLeaf, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          activeClassName={classes.active}
          className={clsx(classes.buttonLeaf, `depth-${depth}`)}
          component={isLogout ? "button" : RouterLink}
          exact
          style={style}
          to={isLogout ? undefined : href}
          onClick={isLogout ? onLogout : undefined}
        >
          <Box className={classes.IcoBoxesMain}>
          {Icon && (
            <span>
              {typeof Icon === 'string' ? <img src={Icon} alt={`${Icon}Img`} width={"19px"} /> : <Icon size={20} />}
            </span>
          )}
        </Box>
          <span className={classes.title}>{title}</span>
          {Info && <Info />}
        </Button>
      </ListItem>
    </>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  isLogout: PropTypes.bool,
  onLogout: PropTypes.func,
};

NavItem.defaultProps = {
  open: false,
};

export default NavItem;
